import * as R from 'ramda';
import Vue from 'vue';
import { getI18N } from '@/ui/i18n/i18n';
import encoders from './encoders';
import { now } from '@/_legacy/util/UnixTimestamps';
import {
  canHaveChildrenHiddenForFormHideAction,
  checkIfAllConditionsAreMet,
  findItem,
  findHideFormAction,
  hasHideFormAction,
  isAllowedToBeHiddenAfterAction,
  isHiddenAfterFormAction,
  isAutomaticResponse
} from '@/_legacy/services/inspection';
import { ResponsesTypes } from '@/entities/checklist';
import { v4 as uuidv4 } from 'uuid';
import { deleteItem } from '@/_legacy/store/deleteItemFn';
import { INSPECTION_ITEM_TYPE, ISSUE_TYPE, INSPECTION_STATUS } from '@/_legacy/constants';
import clamp from 'clamp';
import { extractQuestionUuidFromComposedUuid, findIssueInInspectionByQuestion } from '@/ui/components/issue/helpers';
import { shouldShowConductInspectionHeaderPage } from '@/_legacy/helpers/inspectionHeaderPageVisibilityHelper';
import { INSPECTION_ITEM_RESPONSE } from '../constants';
import { parseNumber } from '../services/inspection';
import InspectionEntityItemsHelper from '@/_legacy/helpers/inspectionEntityItemsHelper';
import { defaultEntityItemQuery } from '@/services/api/resources/entityItem/query';
import { useTask } from '@/ui/concerns';
import { retrieveEntityItems } from '@/services/entityItem/entityItemService';
import { useConductStore } from '../../stores/conduct/conduct';
import { computeTextPrefill, isTextPrefillQuestion } from '@/services/entityPropertyPrefill';

const i18n = getI18N();

// Store reference to the store instance for use in mutations
let storeInstance = null;

export const setStoreInstance = store => {
  storeInstance = store;
};

export default {
  namespaced: true,

  state: getDefaultState(),

  getters: {
    initializing: state => state.initializing,
    inspection: state => state.inspection,
    currentPage: state => state.currentPage,
    errors: state => state.errors,
    inspectionPages: state => state.inspectionPages,
    hideFormTriggeredPage: state => state.hideFormTriggeredPage,
    isLastPage: state => state.currentPage === state.inspectionPages.length - 1,
  },

  actions: {
    validate({ state }) {
      const errors = {};

      //validate all pages up to the current page
      state.inspectionPages.slice().forEach(page => {
        validateItem(page, errors);
      });

      state.errors = errors;
    },

    async load({ commit, dispatch }, { inspection, loggedUser }) {
      commit('setLoggedUser', loggedUser);
      return dispatch('start', {
        ...inspection,
        inspection_id: inspection.inspection_id,
        conducted_at: null,
      });
    },

    async start({ state, commit, dispatch }, inspection) {
      state.initializing = true;
      state.inspection = inspection;
      state.inspection.started_at = state.inspection.started_at ?? now();

      // Normalize allConditionsMet to ensure it's always a boolean
      if (inspection.form && inspection.form.items) {
        normalizeFormItems(inspection.form.items);
      }

      const headerPage = inspection.form.items[0];
      const headerEntityItemQuestion = headerPage.items.find(item => item.response_type === ResponsesTypes.entityItem);
      const headerEntityItemQuery = headerEntityItemQuestion ? buildEntityItemQuery(headerEntityItemQuestion) : null;
      const headerEntityItemQuestionUuid = headerEntityItemQuestion?.uuid || null;
      const wasEntityTypeOfResponseMigrated = inspection.entity_type_of_response_migrated || false;
      const entityItems = InspectionEntityItemsHelper.inspectionEntityItems(state.loggedUser, state.inspection);
      let allowedEntityItems = Object.keys(inspection.entity_items || {}).map(id => Number(id));

      if (headerEntityItemQuery) {
        const { allowedEntityItemIds, entityItems: fetchedEntityItems } = await getAllowedEntitiesForHeaderPage(
          headerEntityItemQuery,
          entityItems
        );

        allowedEntityItems = allowedEntityItemIds;
        commit('setEntityItemsData', {
          items: fetchedEntityItems,
          query: headerEntityItemQuery,
        });
      } else {
        commit('setEntityItemsData', {
          items: [],
          query: null,
        });
      }

      const isHeaderPageVisible = inspection.form.items?.length > 0
        ? shouldShowConductInspectionHeaderPage(
          headerPage,
          state.inspection,
          state.loggedUser,
          allowedEntityItems,
          entityItems
        )
        : false;

      state.inspectionPages = inspection.form.items.slice(isHeaderPageVisible ? 0 : 1);
      state.headerEntityItemQuestionUuid = headerEntityItemQuestionUuid;

      // if no entity item is set, but there is an entity item list, select first entity item
      const hasEntityItemSelectedFromAvailableItems = state.inspection.entity_item == null
        && allowedEntityItems.length > 0;

      if (hasEntityItemSelectedFromAvailableItems && !isHeaderPageVisible && !wasEntityTypeOfResponseMigrated) {
        state.inspection.entity_item = allowedEntityItems[0];
      }

      if (state.inspection.issues) {
        state.inspection.issues = Object.keys(state.inspection.issues).reduce((acc, key) => {
          acc[getActionKey(state.inspection.issues, key)] = state.inspection.issues[key];
          return acc;
        }, {});
      }

      if (!isHeaderPageVisible && headerEntityItemQuestion && allowedEntityItems.length) {
        headerEntityItemQuestion.response = [allowedEntityItems[0]];
      }

      const hasPrefillQuestions = collectPrefillQuestions(state.inspectionPages).length > 0;
      const hasEntityItemSelected = state.inspection.entity_item != null;
      const needsEntityItemsData = state.entityItemsData.length === 0;

      if (hasPrefillQuestions && hasEntityItemSelected && needsEntityItemsData) {
        const query = defaultEntityItemQuery();
        const { entityItems: fetchedEntityItems } = await getAllowedEntitiesForHeaderPage(
          query,
          entityItems
        );

        commit('setEntityItemsData', {
          items: fetchedEntityItems,
          query,
        });
      }

      await dispatch('applyEntityPropertyPrefill');
      state.initializing = false;
    },

    send({
      commit
    }) {
      commit('end');

      /*
      TODO:
      This is a security issue. Anybody can post any form they want.
      What we want instead is to only collect the responses and their
      uuid and then post those. Then find the questions and add the
      responses server side. OR we could try to match up the
      submitted form structure with the one on the server, but that
      seems even more complicated.
      */
    },

    cancel({
      commit,
    }) {
      /*if (state.inspection.id) {
        axios.post(`/nova-api/inspections/${state.inspection.id}/cancel`);
      }*/
      commit('reset');
    },

    issueSaved({ commit }, payload) {
      commit('issueSaved', payload);
    },

    issueRemoved({ commit }, parentId) {
      commit('issueRemoved', parentId);
    },

    async entityItemChanged({ commit, dispatch }, { entityItemId }) {
      commit('entityItemChanged', entityItemId);
      await dispatch('applyEntityPropertyPrefill');
    },

    async applyEntityPropertyPrefill({ state, commit }, { skipHideFormReevaluation = false } = {}) {
      if (!state.inspection) {
        return;
      }

      const applyPrefillForQuestions = prefillEntityItem => {
        const questions = collectPrefillQuestions(state.inspectionPages);
        const prefilledQuestionUuids = [];

        questions.forEach(question => {
          const prefillState = computeTextPrefill(question, prefillEntityItem);

          Vue.set(question, 'prefillReadOnly', prefillState.prefillReadOnly);
          Vue.set(question, 'prefillHidden', prefillState.prefillHidden);

          const isRteField = question.params?.show_rte_response === true;

          commit('changeResponse', {
            item: question.uuid,
            response: isRteField ? '' : prefillState.response,
            htmlResponse: isRteField ? prefillState.response : '',
            rteResponse: isRteField,
            responded: prefillState.responded,
            skipHideFormReevaluation,
          });

          prefilledQuestionUuids.push(question.uuid);
        });

        // After prefilling, re-evaluate conditional logic for prefilled questions
        // This ensures conditional sections that depend on these questions are updated
        if (skipHideFormReevaluation && prefilledQuestionUuids.length > 0) {
          const form = state.inspection.form;
          const conditionalSectionsToReevaluate = new Set();

          // Collect all conditional sections that depend on prefilled questions
          prefilledQuestionUuids.forEach(questionUuid => {
            const question = findItem(form, questionUuid);
            if (question && question.check_conditions_for && question.check_conditions_for.length > 0) {
              question.check_conditions_for.forEach(conditionalSectionUuid => {
                conditionalSectionsToReevaluate.add(conditionalSectionUuid);
              });
            }
          });

          // Re-evaluate each conditional section
          conditionalSectionsToReevaluate.forEach(conditionalSectionUuid => {
            const conditionalSection = findItem(form, conditionalSectionUuid);
            if (conditionalSection && conditionalSection.conditions) {
              // Find the primary item this conditional section depends on (from first condition)
              // In practice, all conditions in a conditional section depend on the same item
              const primaryDependentItemUuid = conditionalSection.conditions[0]?.item;
              if (primaryDependentItemUuid) {
                const dependentItem = findItem(form, primaryDependentItemUuid);
                if (dependentItem) {
                  // Re-evaluate conditional section against its dependent item
                  conditionalSection.allConditionsMet = Boolean(checkIfAllConditionsAreMet(conditionalSection, dependentItem));
                }
              }
            }
          });
        }
      };

      const entityItemId = state.inspection.entity_item;
      if (!entityItemId) {
        applyPrefillForQuestions(null);
        return;
      }

      const entityItemsData = state.entityItemsData || [];
      const normalizedEntityItemId = Number(entityItemId);
      if (!Number.isFinite(normalizedEntityItemId)) {
        applyPrefillForQuestions(null);
        return;
      }

      const entityItem = entityItemsData.find(item => item.id === normalizedEntityItemId) || null;
      applyPrefillForQuestions(entityItem ?? null);
    },

    issueItemModalOpened({ commit }, payload) {
      commit('issueItemModalOpened', payload);
    },

    issueItemModalClosed({ commit }) {
      commit('issueItemModalClosed');
    },
    resetPage({ commit }) {
      commit('resetPage');
    },
    reset({ commit }) {
      commit('reset');
    },
    startFromDraftPosition({ commit }) {
      commit('startFromDraftPosition');
    },
    setAction({ state }, item) {
      if (state.inspection === null) {
        return;
      }

      const findFormItem = (arr, _item) => {
        arr.forEach((element, index, arr) => {
          if (element.uuid === _item.uuid) {
            Vue.set(arr, index, _item);
          }

          if (element.items) {
            findFormItem(element.items, _item);
          }
        });
      };

      findFormItem(state.inspection.form.items, item);
    }
  },
  mutations: {
    setLoggedUser(state, user) {
      state.loggedUser = user;
    },
    resetScrollElementId(state) {
      state.draftScrollElementId = null;
    },
    setEntityItemsData(state, { items, query }) {
      Vue.set(state, 'entityItemsData', items || []);
      Vue.set(state, 'entityItemQuery', query);
    },
    entityItemChanged(state, entityItemId) {
      let normalizedEntityItemId = null;
      const entityItemNumericId = Number(entityItemId);
      if (Number.isFinite(entityItemNumericId) && entityItemNumericId > 0) {
        normalizedEntityItemId = entityItemNumericId;
      }
      Vue.set(state.inspection, 'entity_item', normalizedEntityItemId);
      if (!state.inspection.issues) {
        return;
      }

      Object.keys(state.inspection.issues)
        .forEach(questionId => {
          if (state.inspection.issues[questionId]) {
            if (!state.inspection.issues[questionId].params) {
              Vue.set(state.inspection.issues[questionId], 'params', {});
            }

            Vue.set(state.inspection.issues[questionId].params, 'entity_item', normalizedEntityItemId);
          }
        });
    },

    issueSaved(state, {
      questionId,
      form,
    }) {
      const issues = state.inspection.issues || {};
      if (!state.inspection) {
        return;
      }

      // when we have an array it means that issue is persisted already and should be re-saved for drafts
      // issues might be an array OR an object with question uuids as keys
      if (Object.keys(issues).length) {
        const issuesToSearch = Array.isArray(issues) ? issues : Object.values(issues);
        const issueToUpdate = findIssueInInspectionByQuestion(issuesToSearch, questionId);

        if (issueToUpdate) {
          issueToUpdate.update_required = true;
        }
      }

      Vue.set(state.inspection, 'issues', {
        ...issues,
        [questionId]: encoders.actionIssue({
          ...form,
          entityItem: state.inspection.entity_item,
        }),
      });
    },

    issueRemoved(state, questionId) {
      Vue.set(state.inspection, 'issues', R.omit([questionId], state.inspection.issues));
    },

    showNotesModal(state, item) {
      state.modals.notes.item = item;
      state.modals.notes.visible = true;
    },

    closeNotesModal(state) {
      state.modals.notes.item = null;
      state.modals.notes.visible = false;
    },

    saveNotes(state, notes) {
      notes = notes.trim();
      if (notes.length > 0) {
        Vue.set(state.modals.notes.item, 'notes', notes);
      } else {
        Vue.delete(state.modals.notes.item, 'notes');
      }

      state.modals.notes.item = null;
      state.modals.notes.visible = false;
    },

    showPhotosModal(state, item) {
      state.modals.photos.item = item;
      state.modals.photos.visible = true;
    },

    closePhotosModal(state) {
      state.modals.photos.item = null;
      state.modals.photos.visible = false;
    },

    savePhotos(state, photos) {
      if (photos.length > 0) {
        Vue.set(state.modals.photos.item, 'photos', photos);
      } else {
        Vue.delete(state.modals.photos.item, 'photos');
      }

      Vue.set(state.modals.photos, 'item', null);
      Vue.set(state.modals.photos, 'visible', false);
    },

    showImageModal(state, item) {
      state.modals.image.item = item;
      state.modals.image.visible = true;
    },

    closeImageModal(state) {
      state.modals.image.visible = false;
      state.modals.image.item = null;
    },

    reset(state) {
      Object.assign(state, getDefaultState());
    },

    end(state) {
      state.inspection.conducted_at = now();
    },

    nextPage(state) {
      if (state.inspection.form.items && (state.inspection.form.items.length - 1) > state.currentPage) {
        state.currentPage++;
      }
    },

    prevPage(state) {
      if (state.currentPage > 0) {
        state.currentPage--;
      }
    },

    resetPage(state) {
      state.currentPage = 0;
    },

    startFromDraftPosition(state) {
      let initialPage = 0;
      let scrollElementId = null;
      let lastAnsweredQuestionUuid = null;

      const isAnyAnswered = items => {
        return items.reduce((result, item, idx) => {
          if (item.type === INSPECTION_ITEM_TYPE.SECTION && item.items) {
            return isAnyAnswered(item.items) || result;
          }

          if (item.type === INSPECTION_ITEM_TYPE.CONDITIONAL && Boolean(item.allConditionsMet) && item.items) {
            return isAnyAnswered(item.items) || result;
          }

          if (item.type === INSPECTION_ITEM_TYPE.QUESTION && isAutomaticResponse(item)) {
            return true;
          }

          if (item.type === INSPECTION_ITEM_TYPE.QUESTION) {
            if (item.responded) {
              lastAnsweredQuestionUuid = item.uuid;
              scrollElementId = items[idx + 1]?.uuid || item.uuid;
            }

            return item.responded || result;
          }

          return result;
        }, false);
      };

      initialPage = state.inspection.form.items.slice().reduce((idx, item, currentIdx) => {
        if (item.type !== INSPECTION_ITEM_TYPE.PAGE) {
          return idx;
        }

        return isAnyAnswered(item.items || []) ? currentIdx : idx;
      }, 0);

      if (scrollElementId) {
        if ((state.inspection.form?.items?.length > state.inspectionPages?.length) && (initialPage > 0)) {
          initialPage--;
        }

        const lastQuestionOnPage = findLastQuestion(state.inspectionPages[initialPage].items);

        if (lastQuestionOnPage.uuid === lastAnsweredQuestionUuid
          && ((initialPage + 1) < state.inspectionPages.length)) {
          initialPage++;
        }
      }

      state.draftScrollElementId = scrollElementId;
      state.currentPage = clamp(0, initialPage, state.inspectionPages.length - 1);
    },

    addRepeatableSection(state, payload) {
      const parent = findItem(state.inspection.form, findParent(state.inspection.form, payload.item));
      const { items } = parent;
      const item = findItem(state.inspection.form, payload.item);
      let insert_at = 0;

      for (const i in items) {
        if (items[i].uuid === payload.item) {
          insert_at = parseInt(i);
          break;
        }
      }

      const section = cloneSection(item);

      if (typeof (item.repeated_sections) === 'undefined') {
        item.repeated_sections = [];
      }
      item.repeated_sections.push(section.uuid);

      section.title = `${section.title} ${item.repeated_sections.length}`;

      if (useConductStore().isTranslationEnabled && section.title_translated) {
        section.title_translated = `${section.title_translated} ${item.repeated_sections.length}`;
      }

      section.required = false;
      section.repeated = true;
      section.repeat = false;
      section.repeats = item.uuid;

      items.splice(insert_at, 0, section);

      // Re-evaluate hide form actions when a new repeated section is added
      // The new section might contain questions with hide form actions
      const form = state.inspection.form;
      resetItemsStateForHideFormAction(form.items);
      state.hideFormTriggeredPage = null;

      form.items.some((page, idx) => {
        const hideFormTriggerPage = recursivelyHideItemsForHideFormActions(form, page.items, idx);

        if (hideFormTriggerPage !== null) {
          state.hideFormTriggeredPage = hideFormTriggerPage;

          return true;
        }
      });
    },

    deleteRepeatedSection(state, payload) {
      const item = findItem(state.inspection.form, payload.item);
      const repeatingSection = findItem(state.inspection.form, item.repeats);

      deleteItem(state.inspection.form, payload.item);

      if (typeof (repeatingSection.repeated_sections) !== 'undefined') {
        const index = repeatingSection.repeated_sections.indexOf(item.uuid);

        if (index !== -1) {
          repeatingSection.repeated_sections.splice(index, 1);
        }

        const store = useConductStore();
        for (const i in repeatingSection.repeated_sections) {
          const repeatedSection = findItem(state.inspection.form, repeatingSection.repeated_sections[i]);
          repeatedSection.title = `${repeatingSection.title} ${parseInt(i) + 1}`;

          if (store.isTranslationEnabled && repeatingSection.title_translated) {
            repeatedSection.title_translated = `${repeatingSection.title_translated} ${parseInt(i) + 1}`;
          }
        }
      }
    },

    changeResponse(state, payload) {
      const item = findItem(state.inspection.form, payload.item);
      Vue.set(item, 'response', payload.response);
      Vue.set(item, 'html_response', payload.htmlResponse);
      Vue.set(item, 'responded', payload.responded);

      if (payload.rteResponse) {
        Vue.set(item, 'rte_response', true);
      } else {
        Vue.delete(item, 'rte_response');
      }

      // Skip hide form re-evaluation if this is being called from prefill logic
      if (payload.skipHideFormReevaluation) {
        return;
      }

      const form = state.inspection.form;
      let shouldReevaluateHideFormActions = false;

      if (item.check_conditions_for.length > 0) {
        for (const i in item.check_conditions_for) {
          const conditionalSection = findItem(form, item.check_conditions_for[i]);
          if (conditionalSection) {
            const previousAllConditionsMet = Boolean(conditionalSection.allConditionsMet);
            const conditionalHasHideFormActions = hasHideFormAction(conditionalSection);
            conditionalSection.allConditionsMet = Boolean(checkIfAllConditionsAreMet(conditionalSection, item));
            conditionalSection.triggeredHideForm = conditionalHasHideFormActions && conditionalSection.allConditionsMet;

            // Re-evaluate hide form actions if:
            // 1. The conditional section has hide form actions, OR
            // 2. The conditional section's visibility changed (items inside might have hide form actions)
            if (conditionalHasHideFormActions || previousAllConditionsMet !== conditionalSection.allConditionsMet) {
              shouldReevaluateHideFormActions = true;
            }
          }
        }
      }

      // Re-evaluate all hide form actions when conditional section visibility changes
      // This ensures items inside conditional sections are properly evaluated
      if (shouldReevaluateHideFormActions) {
        const previousHideFormTriggeredPage = state.hideFormTriggeredPage;

        resetItemsStateForHideFormAction(form.items);
        state.hideFormTriggeredPage = null;

        form.items.some((page, idx) => {
          const hideFormTriggerPage = recursivelyHideItemsForHideFormActions(form, page.items, idx);

          if (hideFormTriggerPage !== null) {
            state.hideFormTriggeredPage = hideFormTriggerPage;

            return true;
          }
        });

        // If hide form was removed (previously triggered, now not triggered), trigger prefill
        const hideFormWasRemoved = previousHideFormTriggeredPage !== null && state.hideFormTriggeredPage === null;

        if (hideFormWasRemoved && storeInstance) {
          // Dispatch prefill action with skipHideFormReevaluation flag to prevent recursion
          Vue.nextTick(() => {
            storeInstance.dispatch('conduct/applyEntityPropertyPrefill', { skipHideFormReevaluation: true });
          });
        }
      }
    },

    setInitialHideFormState(state) {
      const form = state.inspection.form;

      resetItemsStateForHideFormAction(form.items);
      state.hideFormTriggeredPage = null;

      form.items.some((page, idx) => {
        const hideFormTriggerPage = recursivelyHideItemsForHideFormActions(form, page.items, idx);

        if (hideFormTriggerPage !== null) {
          state.hideFormTriggeredPage = idx;

          return true;
        }
      });
    },

    clearInspectionDataOnHideFormTrigger(state) {
      const clearData = item => {
        if (isHiddenAfterFormAction(item)) {
          Vue.set(item, 'response', null);
          Vue.set(item, 'notes', null);
          Vue.set(item, 'responded', false);
          Vue.set(item, 'photos', []);

          if (state.inspection.issues && state.inspection.issues[item.uuid]) {
            Vue.delete(state.inspection.issues, item.uuid);
          }
        }

        if (item.items) {
          item.items.forEach(subItem => clearData(subItem));
        }
      };

      state.inspection.form.items.forEach(item => clearData(item));
    },

    issueItemModalOpened(state, item) {
      Vue.set(state.modals, 'issueItem', {
        visible: true,
        item,
      });
    },

    issueItemModalClosed(state) {
      Vue.set(state.modals, 'issueItem', {
        visible: false,
        item: null,
      });
    },
  },

};

function hideItemsDueToHideFormAction(formItems, currentPage, item) {
  const uuid = item.uuid;

  const recursiveHide = items => {
    let wasFoundBefore = false;
    // this prevents us from going deeper into the tree if we are on same level of condition
    let wasFinalLeafFound = false;

    items.forEach(subItem => {
      // all subItems after "hide" action should be hidden
      if (wasFoundBefore) {
        setHideAfterAction(subItem, true);

        return;
      }

      // if we found subItem - mark it as found before
      if (subItem.uuid === uuid) {
        wasFinalLeafFound = true;
        wasFoundBefore = true; // Set this immediately; the flag is propagated up by returning it from recursiveHide

        return;
      }

      // this is used to skip the iteration of the "children" of the hide forms actions (multiple conditions)
      if (wasFinalLeafFound) {
        wasFoundBefore = true;

        return;
      }

      if (!subItem.items) {
        return;
      }

      // if we found did not reach a final level - go recursively deeper
      if (!wasFinalLeafFound) {
        wasFoundBefore = recursiveHide(subItem.items);
      }
    });

    return wasFoundBefore;
  };

  formItems.forEach((page, idx) => {
    if (idx > currentPage) {
      page.items.forEach(item => setHideAfterAction(item, true));

      return;
    }

    if (idx === currentPage) {
      recursiveHide(page.items);
    }
  });
}

function recursivelyHideItemsForHideFormActions(form, target, pageIdx, parentConditionalSection = null) {
  let hideFormTriggerPage = null;

  target.some(item => {
    // Skip items inside conditional sections that are not visible
    if (parentConditionalSection && (
      parentConditionalSection.allConditionsMet === false
      || parentConditionalSection.allConditionsMet === null
    )) {
      return false;
    }

    // Skip conditional sections that are not visible
    if (item.type === INSPECTION_ITEM_TYPE.CONDITIONAL && (
      item.allConditionsMet === false
      || item.allConditionsMet === null
    )) {
      return false;
    }

    // Skip repeatable section templates (only repeated instances are visible)
    if (item.repeat) {
      return false;
    }

    if (item.check_conditions_for && item.check_conditions_for.length > 0) {
      for (const i in item.check_conditions_for) {
        const conditionalSection = findItem(form, item.check_conditions_for[i]);

        if (conditionalSection) {
          const hasAnchor = findHideFormAction(conditionalSection);

          conditionalSection.triggeredHideForm = hasAnchor
            && Boolean(checkIfAllConditionsAreMet(conditionalSection, item));

          if (conditionalSection.triggeredHideForm) {
            hideFormTriggerPage = pageIdx;
            hideItemsDueToHideFormAction(form.items, pageIdx, hasAnchor);

            return true;
          }
        }
      }
    }

    // if the item is a valid target with subItems
    if (canHaveChildrenHiddenForFormHideAction(item) && item.items) {
      // Pass the current item as parent if it's a conditional section
      const currentParent = item.type === INSPECTION_ITEM_TYPE.CONDITIONAL ? item : parentConditionalSection;
      hideFormTriggerPage = recursivelyHideItemsForHideFormActions(form, item.items, pageIdx, currentParent);

      return hideFormTriggerPage;
    }

    return false;
  });

  return hideFormTriggerPage;
}

function setHideAfterAction(item, hide) {
  item.items?.forEach(child => setHideAfterAction(child, hide));

  if (isAllowedToBeHiddenAfterAction(item)) {
    Vue.set(item, 'hideAfterAction', hide);
  }
}

function resetItemsStateForHideFormAction(formItems) {
  formItems.forEach(item => setHideAfterAction(item, false));
}

function normalizeAllConditionsMet(item) {
  if (item.type === INSPECTION_ITEM_TYPE.CONDITIONAL && item.allConditionsMet !== undefined && item.allConditionsMet !== null) {
    // Ensure allConditionsMet is always a boolean, not a string
    item.allConditionsMet = item.allConditionsMet === true || item.allConditionsMet === 'true' || item.allConditionsMet === 1 || item.allConditionsMet === '1';
  }

  if (item.items) {
    item.items.forEach(child => normalizeAllConditionsMet(child));
  }
}

function normalizeFormItems(formItems) {
  formItems.forEach(page => {
    if (page.items) {
      page.items.forEach(item => normalizeAllConditionsMet(item));
    }
  });
}

function isExcludedFromValidation(item) {
  return item.response_type === ResponsesTypes.qrCode || item.hideAfterAction === true || item.prefillHidden === true;
}

function getDefaultState() {
  return {
    initializing: true,
    hideFormTriggeredPage: 0,
    inspection: null,
    inspectionPages: [],
    currentPage: 0,
    draftScrollElementId: null,
    loggedUser: null,
    errors: {},
    entityItemsData: [],
    entityItemQuery: null,
    headerEntityItemQuestionUuid: null,
    modals: {
      notes: {
        visible: false,
        item: null,
      },
      photos: {
        visible: false,
        item: null,
      },
      cancel: { visible: false },
      issueItem: {
        visible: false,
        item: null,
      },
      image: {
        visible: false,
        item: null,
      }
    },
  };
}

function getActionKey(issues, key) {
  return issues[key].payload?.type !== ISSUE_TYPE.AUTOMATIC
    ? extractQuestionUuidFromComposedUuid(issues[key])
    : key;
}

function cloneSection(originalSection) {
  let stringifiedSection = JSON.stringify(originalSection);

  stringifiedSection = reassignUUIDs(originalSection, stringifiedSection);

  return JSON.parse(stringifiedSection);
}

function reassignUUIDs(item, text) {
  if (item.uuid) {
    const new_uuid = uuidv4();
    text = text.replace(new RegExp(item.uuid, 'g'), new_uuid);
  }
  if (item.type === 'section' && item.node_id) {
    text = text.replace(new RegExp(`"${item.node_id}"`, 'g'), null);
  }

  if (item.items && item.items.length > 0) {
    for (const i in item.items) {
      text = reassignUUIDs(item.items[i], text);
    }
  }

  return text;
}

function findLastQuestion(items) {
  return items.reduce((lastQuestion, item) => {
    if (item.type === INSPECTION_ITEM_TYPE.QUESTION) {
      return item;
    }

    if (item.items) {
      return findLastQuestion(item.items) || lastQuestion;
    }

    return lastQuestion;
  }, null);
}

function validateItem(item, errors) {
  if (!item) {
    return;
  }

  if (isExcludedFromValidation(item)) {
    return;
  }

  const itemErrors = getErrorsForItem(item);

  if (itemErrors.length > 0) {
    errors[item.uuid] = itemErrors;
  }

  // don't check repeatable section items
  if (item.repeat) {
    return;
  }

  // don't check conditional sections that are not displayed
  if (
    item.allConditionsMet === false
    || item.allConditionsMet === null
  ) {
    return;
  }

  // check all subitems
  if (item.items) {
    for (const i in item.items) {
      validateItem(item.items[i], errors);
    }
  }
}

function getErrorsForItem(item) {
  const errors = [];

  if (!item) {
    return errors;
  }

  if (item.type === 'question') {
    // required
    if (
      item.required === true
      && item.responded !== true
    ) {
      errors.push(i18n.t('This question is required.'));
    }

    // numbers
    if (
      item.response_type === INSPECTION_ITEM_RESPONSE.NUMBER
      && item.responded === true
      && isNaN(parseNumber(item.response))
    ) {
      errors.push(i18n.t('Response must be a number. At most one decimal separator, and no thousand separator.'));
    }
  } else if (item.type === 'section') {
    if (
      item.required === true
      && item.repeat === true
      && (
        typeof (item.repeated_sections) === 'undefined'
        || item.repeated_sections.length === 0
      )
    ) {
      // disabled until propery specified
      // errors.push(i18n.t("This section is required."))
    }
  }

  return errors;
}

function findParent(item, uuid) {
  if (!item.items) {
    return false;
  }

  for (const i in item.items) {
    if (item.items[i].uuid === uuid) {
      return item.uuid;
    }
    const res = findParent(item.items[i], uuid);
    if (res !== false) {
      return res;
    }
  }

  return false;
}

async function getAllowedEntitiesForHeaderPage(query, inspectionEntityItems) {
  const fetchEntityItemsTask = useTask(
    () => retrieveEntityItems(query, true),
    resp => resp.data,
    uuidv4(),
    e => {
      console.error('error', e);
    }
  );

  const result = await fetchEntityItemsTask.run();
  const entityItems = result?.data || [];

  const allowedEntityItemIds = inspectionEntityItems.filter(id => {
    return entityItems.some(entityItem => entityItem.id === id);
  });

  return {
    allowedEntityItemIds,
    entityItems,
  };
}

function buildEntityItemQuery(entityItemQuestion) {
  const query = defaultEntityItemQuery();

  if (!entityItemQuestion) {
    return query;
  }

  if (Array.isArray(entityItemQuestion.entity_types)) {
    query.entityType = entityItemQuestion.entity_types;
  }

  if (Array.isArray(entityItemQuestion.entity_folders)) {
    query.entityFolderRecursive = entityItemQuestion.entity_folders;
  }

  return query;
}

function collectPrefillQuestions(pages) {
  if (!Array.isArray(pages)) {
    return [];
  }

  const questions = [];

  const visit = items => {
    if (!Array.isArray(items)) {
      return;
    }

    items.forEach(item => {
      if (isTextPrefillQuestion(item)) {
        questions.push(item);
      }

      if (Array.isArray(item.items)) {
        visit(item.items);
      }

      if (Array.isArray(item.repeated_sections)) {
        item.repeated_sections.forEach(section => visit(section.items));
      }
    });
  };

  pages.forEach(page => visit(page?.items));

  return questions;
}
